import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useSupabaseAuth } from '../lib/useSupabaseAuth.jsx'
import { PublicRoutes } from './PublicRoutes'
import ProtectedRoutes from './ProtectedRoutes'
import SimpleOnboarding from './SimpleOnboarding'
import OnboardingLayout from './OnboardingLayout'
import { isFeatureEnabled } from '../lib/featureFlags'
import { useIsStandalone } from '../lib/useIsStandalone'
import { getPostSignInPath } from '../lib/postSignIn'

const MainRouter = () => {
  return <MainRouterContent />
}

const MainRouterContent = () => {
  const { user, isAuthenticated, authReady, isSigningUp } = useSupabaseAuth()
  const location = useLocation()
  const isStandalone = useIsStandalone()

  // Check if user is trying to access a protected route while unauthenticated
  const isProtectedRoute = location.pathname.startsWith('/dashboard') || 
                          location.pathname.startsWith('/clients') ||
                          location.pathname.startsWith('/jobs') ||
                          location.pathname.startsWith('/estimates') ||
                          location.pathname.startsWith('/invoices') ||
                          location.pathname.startsWith('/employees') ||
                          location.pathname.startsWith('/settings') ||
                          location.pathname.startsWith('/expenses') ||
                          location.pathname.startsWith('/scheduling') ||
                          location.pathname.startsWith('/automations') ||
                          location.pathname.startsWith('/files') ||
                          location.pathname.startsWith('/integrations') ||
                          location.pathname.startsWith('/reports') ||
                          location.pathname.startsWith('/subscription') ||
                          location.pathname.startsWith('/widgets') ||
                          // Conditionally include toolbelt routes
                          (isFeatureEnabled('TOOLBELT_ENABLED') && location.pathname.startsWith('/toolbelt')) ||
                          location.pathname.startsWith('/ai')

  // Check if user is accessing onboarding routes
  const isOnboardingRoute = location.pathname.startsWith('/onboarding')

  // Check if user is accessing a public magic link route or payment success page
  const isPublicMagicLink = location.pathname.startsWith('/public/estimate/') ||
                           location.pathname.startsWith('/public/invoice/') ||
                           location.pathname.startsWith('/payment/success') ||
                           location.pathname.startsWith('/payment/deposit-success') ||
                           location.pathname.startsWith('/email-verification') ||
                           location.pathname === '/reset-password' ||
                           location.pathname === '/optin-workflow-twilio' ||
                           location.pathname === '/integrations/quickbooks/callback'

  // Check if user is accessing a public route that authenticated users should also be able to access
  const isPublicRoute = location.pathname === '/faqs' ||
                        location.pathname === '/pricing' ||
                        location.pathname === '/features' ||
                        location.pathname === '/about' ||
                        location.pathname === '/blog' ||
                        location.pathname === '/prosperia-ai' ||
                        location.pathname === '/pwa-welcome' ||
                        location.pathname === '/support' ||
                        location.pathname === '/quickquote' ||
                        location.pathname === '/'

  // Check if running as PWA (standalone mode)
  const checkIsStandalone = () => {
    try {
      const matchStandalone = window.matchMedia('(display-mode: standalone)')
      const isMatch = matchStandalone?.matches === true
      const isiOSStandalone = (navigator && navigator.standalone === true) || false
      return isMatch || isiOSStandalone
    } catch {
      return false
    }
  }

  // Redirect authenticated users from root to dashboard (including PWA users)
  useEffect(() => {
    if (authReady && isAuthenticated && !isSigningUp && location.pathname === '/') {
      console.log('[MainRouter] Redirecting authenticated user from root')
      window.location.replace(getPostSignInPath(user))
      return
    }
  }, [authReady, isAuthenticated, isSigningUp, location.pathname, user])

  // Redirect PWA users from root to PWA welcome page (only for unauthenticated users)
  // Note: This must run before the general public route handling
  useEffect(() => {
    const isPWA = checkIsStandalone() || isStandalone
    if (authReady && !isAuthenticated && !isSigningUp && isPWA && location.pathname === '/') {
      console.log('[MainRouter] Redirecting unauthenticated PWA user from root to PWA welcome page')
      window.location.replace('/pwa-welcome')
      return
    }
  }, [authReady, isAuthenticated, isSigningUp, isStandalone, location.pathname])

  // Redirect unauthenticated users trying to access protected routes
  useEffect(() => {
    if (authReady && !isAuthenticated && !isSigningUp && isProtectedRoute && !isPublicMagicLink) {
      // Clear any cached authentication data that might be causing issues
      try {
        localStorage.removeItem('supabase.auth.token')
        sessionStorage.clear()
      } catch (error) {
        console.warn('Could not clear auth cache:', error)
      }
      
      // Redirect to home page with a message
      window.location.href = '/?message=session_expired'
    }
  }, [authReady, isAuthenticated, isSigningUp, isProtectedRoute, isPublicMagicLink])

  // No longer redirecting to optional onboarding - users go directly to dashboard

  // Show loading state while auth is initializing. `loading` is not part of
  // this: signIn/signUp/signOut set it too, and swapping the page for a loader
  // unmounted the sign-in modal mid-attempt, discarding its error (a wrong
  // password, or the unconfirmed-email prompt from #428) before it rendered.
  if (!authReady && !isSigningUp) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-mint-50 to-primary-50">
        <div className="text-center">
          <div className="w-8 h-8 rounded-full border-4 border-primary-200 border-t-primary-500 animate-spin mb-4 mx-auto"></div>
          <p className="text-secondary font-medium">Preparing application...</p>
          <p className="text-sm text-muted mt-2">Please wait while we verify your session</p>
        </div>
      </div>
    )
  }

  // Show onboarding routes for unauthenticated users only
  if (isOnboardingRoute) {
    if (!isAuthenticated) {
      console.log('[MainRouter] Rendering SimpleOnboarding for unauthenticated user')
      return (
        <OnboardingLayout>
          <SimpleOnboarding />
        </OnboardingLayout>
      )
    } else {
      // Authenticated users should not be on onboarding route - redirect to dashboard
      console.log('[MainRouter] Redirecting authenticated user from onboarding to dashboard')
      window.location.href = '/dashboard'
    }
  }

  // Show public magic link routes and payment success page even for authenticated users
  if (isPublicMagicLink) {
    return <PublicRoutes />
  }

  // Show public routes (like FAQs, pricing, etc.) for both authenticated and unauthenticated users
  // But exclude root path for authenticated users (they should go to dashboard)
  if (isPublicRoute) {
    // If authenticated user is on root, show loading while redirect happens
    if (isAuthenticated && location.pathname === '/') {
      // Redirect is handled by useEffect above, show loading state while redirecting
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-mint-50 to-primary-50">
          <div className="text-center">
            <div className="w-8 h-8 rounded-full border-4 border-primary-200 border-t-primary-500 animate-spin mb-4 mx-auto"></div>
            <p className="text-secondary font-medium">Redirecting to dashboard...</p>
          </div>
        </div>
      )
    }
    // Unauthenticated users can access both / and /home - both render LandingPage
    return <PublicRoutes />
  }

  // Show public routes for unauthenticated users
  if (!isAuthenticated && !isSigningUp) {
    return <PublicRoutes />
  }

  // Show protected routes for authenticated users
  return <ProtectedRoutes />
}

export default MainRouter 